import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import {connect} from 'react-redux';
import ReactTooltip from 'react-tooltip';
import BookingStatus from 'components/BookingStatus';
import {reverse} from 'utils/urls';
import {formatDateForApi, formatDate, DATE_FORMAT_API} from 'utils/datetime';

import {BOOKING_SET_DETAIL_URL} from 'config/urls';

import {
  checkOutTenants,
  fetchDepartures,
  selectDepartures,
  selectDeparturesLoading,
  fetchCheckedOutTodayDepartures,
  selectCheckedOutTodayDepartures,
  selectCheckedOutTodayDeparturesLoading,
  undoCheckOutTenants
} from 'ducks/frontDesk';
import {undoCheckOutTenantsEarly} from 'ducks/bookings';
import {exportCsv, selectExporting, exportCsvInBackground} from 'ducks/files';
import {
  isModalOpen,
  openModal,
  closeModal,
  MODALS as BASE_MODALS
} from 'ducks/modals';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import {
  exportDeparturesCsv as exportDeparturesCsvSdk,
  exportDeparturesCsvInBackground as exportDeparturesCsvInBackgroundSdk
} from 'sdk/frontDesk';

import Table from 'components/Table';
import Button from 'components/Button';
import Tooltip from 'components/Tooltip';
import Loading from 'components/Loading';
import {withSorting, SORTING, WithSortingProps} from 'components/withSorting';
import {
  EmptyListPlaceholder,
  TableSplitter,
  Owed,
  TenantCell,
  BookingType,
  BookingSetBalance
} from 'containers/FrontDesk/components/shared';
import NotesCount from 'components/NotesCount';
import MemberCheckOutModal from 'components/MemberCheckOutModal';
import ClickIconWithNoPropagation from 'components/ClickIconWithNoPropagation';

import './styles.css';

const DEPARTURES_CSV_FILENAME = 'departures';
const TOOLTIP_NAME = 'departuresTableTooltip';

const DeparturesRow = ({
  data: {
    bookingId,
    bookingSetId,
    tenancyId,
    primaryTenant,
    secondaryTenant,
    room,
    status,
    bookingType,
    departureDate,
    notesCount,
    hasUnresolvedNotes,
    bookingSetBalance,
    bookingSetRentAndOneOffBalance,
    bookingSetDepositBalance,
    isEarlyCheckOut
  },
  openMemberCheckOutModal,
  openBookingNotesModal,
  redirectToBookingSet,
  undoCheckOutTenants,
  undoCheckOutTenantsEarly
}: any) => (
  <Table.Row onClick={() => redirectToBookingSet({bookingSetId})}>
    <Table.Cell className="name">
      <TenantCell
        primaryTenant={primaryTenant}
        secondaryTenant={secondaryTenant}
      />
    </Table.Cell>
    <Table.Cell className="type">
      <div
        data-tip={JSON.stringify({type: 'bookingType', content: bookingType})}
        data-for={TOOLTIP_NAME}>
        <BookingType bookingType={bookingType} />
      </div>
    </Table.Cell>
    <Table.Cell className="departure">{formatDate(departureDate)}</Table.Cell>
    <Table.Cell className="room">{room}</Table.Cell>
    <Table.Cell className="balance">
      <div
        data-tip={JSON.stringify({
          bookingSetRentAndOneOffBalance,
          bookingSetDepositBalance
        })}
        data-for={TOOLTIP_NAME}>
        <Owed amount={bookingSetBalance} />
      </div>
    </Table.Cell>
    <Table.Cell className="notesCount">
      <NotesCount
        notesCount={notesCount}
        showNotification={hasUnresolvedNotes}
        onClick={() => openBookingNotesModal(bookingId)}
      />
    </Table.Cell>
    <Table.Cell className="status">
      <BookingStatus status={status} />
    </Table.Cell>
    <Table.Cell className="checkOut">
      {openMemberCheckOutModal && (
        <ClickIconWithNoPropagation
          onClick={() => openMemberCheckOutModal(bookingId, tenancyId)}>
          <Button white={true}>Check out</Button>
        </ClickIconWithNoPropagation>
      )}
      {undoCheckOutTenants &&
        (isEarlyCheckOut ? (
          <ClickIconWithNoPropagation
            onClick={() => undoCheckOutTenantsEarly(bookingId)}>
            <Button className="checkOut__undo" white>
              Undo early check out
            </Button>
          </ClickIconWithNoPropagation>
        ) : (
          <ClickIconWithNoPropagation
            onClick={() => undoCheckOutTenants(bookingId)}>
            <Button className="checkOut__undo" white>
              Undo check out
            </Button>
          </ClickIconWithNoPropagation>
        ))}
    </Table.Cell>
  </Table.Row>
);

interface DeparturesTableProps {
  filter: {[key: string]: any};
  locationRef: string;
}

class DeparturesTable extends React.Component<
  DeparturesTableProps &
    WithSortingProps &
    ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps
> {
  state = {
    isOpened: false
  };

  getOption = () => this.props.filter.value;

  isToday = () => this.props.filter.value === 'today';

  hideCheckedOut = () => {
    this.setState({isOpened: false});
  };

  showCheckedOut = () => {
    this.setState({isOpened: true}, this.fetchCheckedOutTodayDepartures);
  };

  fetchDeparturesExtraActions = () => {
    const {locationRef} = this.props;

    return [
      () =>
        fetchDepartures({
          locationRef,
          option: this.getOption(),
          dateFrom: formatDateForApi(this.props.filter.dateFrom),
          dateTo: formatDateForApi(this.props.filter.dateTo)
        }),
      () => fetchCheckedOutTodayDepartures({locationRef})
    ];
  };

  openMemberCheckOutModal = (bookingId, tenancyId) => {
    const {locationRef} = this.props;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.CREATE_CHECK_OUT,
        locationRef
      },
      () => {
        this.props.openModal(
          BASE_MODALS.MEMBER_CHECK_OUT,
          {
            locationRef,
            bookingId,
            confirmAction: extraActions =>
              this.props.checkOutTenants({tenancyId}, extraActions)
          },
          true
        );
      }
    );
  };

  fetchDepartures = () => {
    return this.props.fetchDepartures({
      locationRef: this.props.locationRef,
      option: this.getOption(),
      dateFrom: formatDateForApi(this.props.filter.dateFrom),
      dateTo: formatDateForApi(this.props.filter.dateTo)
    });
  };

  fetchCheckedOutTodayDepartures = () => {
    // Don't refetch checked-out today departures if they are already present
    if (_.isEmpty(this.props.checkedOutTodayDepartures)) {
      this.props.fetchCheckedOutTodayDepartures({
        locationRef: this.props.locationRef
      });
    }
  };

  exportDeparturesCsv = () => {
    this.props.exportCsv({
      exportData: {
        locationRef: this.props.locationRef,
        option: this.getOption(),
        dateFrom: formatDateForApi(this.props.filter.dateFrom),
        dateTo: formatDateForApi(this.props.filter.dateTo)
      },
      exportSdk: exportDeparturesCsvSdk,
      fileName: DEPARTURES_CSV_FILENAME
    });
  };

  exportDeparturesCsvInBackground = () => {
    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_CSV_EXPORT_FILE
      },
      () =>
        this.props.exportCsvInBackground({
          exportData: {
            locationRef: this.props.locationRef,
            option: this.getOption(),
            dateFrom: formatDateForApi(this.props.filter.dateFrom),
            dateTo: formatDateForApi(this.props.filter.dateTo)
          },
          exportSdk: exportDeparturesCsvInBackgroundSdk,
          fileName: DEPARTURES_CSV_FILENAME
        })
    );
  };

  openBookingNotesModal = bookingId => {
    const {locationRef} = this.props;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_BOOKING_NOTES,
        locationRef
      },
      () => {
        this.props.openModal(BASE_MODALS.BOOKING_NOTES, {
          bookingId,
          locationRef,
          refetchExtraAction: this.fetchDeparturesExtraActions
        });
      }
    );
  };

  componentDidMount() {
    this.fetchDepartures();
  }

  componentDidUpdate(prevProps) {
    if (this.props.filter.value !== prevProps.filter.value && !this.isToday()) {
      this.setState({isOpened: false});
    }

    if (
      this.props.filter.value !== prevProps.filter.value ||
      this.props.filter.dateFrom !== prevProps.filter.dateFrom ||
      this.props.filter.dateTo !== prevProps.filter.dateTo
    ) {
      this.fetchDepartures();
    }

    const departuressAreFetched = !_.isEqual(
      prevProps.departures,
      this.props.departures
    );
    const departuresForTodayAreFetched = !_.isEqual(
      prevProps.checkedOutTodayDepartures,
      this.props.checkedOutTodayDepartures
    );
    if (departuressAreFetched || departuresForTodayAreFetched) {
      ReactTooltip.rebuild();
    }
  }

  getSortedTableData = data => {
    const sort = this.props.getSort();

    if (sort.field === 'departureDate' && sort.direction === '+') {
      return _.sortBy(data, [el => moment(el.departureDate, DATE_FORMAT_API)]);
    }

    if (sort.field === 'departureDate' && sort.direction === '-') {
      return _.sortBy(data, [el => -moment(el.departureDate, DATE_FORMAT_API)]);
    }

    return data;
  };

  redirectToBookingSet = ({bookingSetId}) => {
    const {locationRef} = this.props;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_BOOKING, locationRef},
      () => {
        window.open(
          reverse(BOOKING_SET_DETAIL_URL, {locationRef, bookingSetId})
        );
      }
    );
  };

  undoCheckOutTenants = bookingId => {
    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading: 'Undo check out member',
      question: 'Are you sure you want to undo this check out?',
      confirmAction: () =>
        this.props.undoCheckOutTenants({bookingId}, [
          ...this.fetchDeparturesExtraActions(),
          () => closeModal(BASE_MODALS.CONFIRM)
        ])
    });
  };

  undoCheckOutTenantsEarly = bookingId => {
    const {locationRef} = this.props;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.CREATE_CHECK_OUT,
        locationRef
      },
      () => {
        this.props.openModal(BASE_MODALS.CONFIRM, {
          heading: 'Undo early checkout',
          question: 'Are you sure you want to undo this early checkout?',
          confirmAction: () =>
            this.props.undoCheckOutTenantsEarly({bookingId}, [
              ...this.fetchDeparturesExtraActions(),
              () => closeModal(BASE_MODALS.CONFIRM)
            ])
        });
      }
    );
  };

  fetchTooltipData = data =>
    !_.isEmpty(data) && (
      <BookingSetBalance
        bookingSetRentAndOneOffBalance={data.bookingSetRentAndOneOffBalance}
        bookingSetDepositBalance={data.bookingSetDepositBalance}
      />
    );

  render() {
    const {isOpened} = this.state;
    const {
      departuresLoading,
      checkedOutTodayDeparturesLoading,
      exportingCsv,
      modals: {isMemberCheckOutModalOpen}
    } = this.props;

    const departures = this.getSortedTableData(this.props.departures);
    const checkedOutTodayDepartures = this.getSortedTableData(
      this.props.checkedOutTodayDepartures
    );

    return (
      <div className="DeparturesTable">
        <div className="heading">
          <div className="label">Departures</div>
        </div>

        <Table>
          <Table.Header>
            <Table.HeaderItem heading="name" className="name" />
            <Table.HeaderItem heading="type" className="type" />
            <Table.HeaderItem
              heading="departure"
              className="departure"
              sortDirection={
                this.props.getSort().field === 'departureDate' &&
                this.props.getSort().direction
              }
              onSort={() => this.props.onSort('departureDate')}
            />
            <Table.HeaderItem heading="room no" className="room" />
            <Table.HeaderItem heading="owed" className="balance" />
            <Table.HeaderItem heading="notes" className="notesCount" />
            <Table.HeaderItem heading="status" className="status" />
            <Table.HeaderItem heading="check-out" className="checkOut" />
          </Table.Header>
        </Table>
        <div className="departures">
          {departuresLoading && <Loading />}
          {!departuresLoading &&
            _.isEmpty(departures) && <EmptyListPlaceholder />}
          {!departuresLoading &&
            !_.isEmpty(departures) && (
              <Table>
                <Table.Body>
                  {departures.map(booking => (
                    <DeparturesRow
                      key={booking.bookingId.toString()}
                      openMemberCheckOutModal={this.openMemberCheckOutModal}
                      data={booking}
                      openBookingNotesModal={this.openBookingNotesModal}
                      redirectToBookingSet={this.redirectToBookingSet}
                    />
                  ))}
                </Table.Body>
              </Table>
            )}
        </div>

        <TableSplitter
          isOpened={isOpened}
          showSplitButton={this.isToday()}
          splitButtonText="Checked out"
          triggerShow={this.showCheckedOut}
          triggerHide={this.hideCheckedOut}
          exportDisabled={
            departuresLoading || exportingCsv || _.isEmpty(departures)
          }
          exportButtonTitle="Export departures"
          triggerExport={this.exportDeparturesCsv}
          triggerExperimentalExport={this.exportDeparturesCsvInBackground}
        />

        {isOpened && (
          <div className="checkedOutToday">
            {checkedOutTodayDeparturesLoading && <Loading />}
            {!checkedOutTodayDeparturesLoading &&
              _.isEmpty(checkedOutTodayDepartures) && <EmptyListPlaceholder />}
            {!checkedOutTodayDeparturesLoading &&
              !_.isEmpty(checkedOutTodayDepartures) && (
                <Table>
                  <Table.Body>
                    {checkedOutTodayDepartures.map(booking => (
                      <DeparturesRow
                        key={booking.bookingId.toString()}
                        data={booking}
                        openBookingNotesModal={this.openBookingNotesModal}
                        redirectToBookingSet={this.redirectToBookingSet}
                        undoCheckOutTenants={this.undoCheckOutTenants}
                        undoCheckOutTenantsEarly={this.undoCheckOutTenantsEarly}
                      />
                    ))}
                  </Table.Body>
                </Table>
              )}
          </div>
        )}

        {isMemberCheckOutModalOpen && (
          <MemberCheckOutModal
            getExtraActions={this.fetchDeparturesExtraActions}
          />
        )}
        <Tooltip
          place="left"
          effect="solid"
          id={TOOLTIP_NAME}
          getContent={tooltipData => {
            const data = tooltipData && JSON.parse(tooltipData);
            const content =
              data && data.type === 'bookingType'
                ? data.content
                : this.fetchTooltipData(data);
            return content;
          }}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  exportingCsv: selectExporting(state, DEPARTURES_CSV_FILENAME),
  departures: selectDepartures(state),
  departuresLoading: selectDeparturesLoading(state),
  checkedOutTodayDepartures: selectCheckedOutTodayDepartures(state),
  checkedOutTodayDeparturesLoading: selectCheckedOutTodayDeparturesLoading(
    state
  ),
  modals: {
    isMemberCheckOutModalOpen: isModalOpen(state, BASE_MODALS.MEMBER_CHECK_OUT)
  }
});

const mapDispatchToProps = {
  fetchDepartures,
  requiresPermission,
  openModal,
  fetchCheckedOutTodayDepartures,
  exportCsv,
  exportCsvInBackground,
  checkOutTenants,
  undoCheckOutTenants,
  undoCheckOutTenantsEarly
};

export default withSorting(
  connect(mapStateToProps, mapDispatchToProps)(DeparturesTable),
  SORTING.DEPARTURES_TABLE
) as React.ComponentType<DeparturesTableProps>;
